import { focusHaloFlush, panelMotion, press } from "@pax/ui";
import { bisector, max, min, sum } from "d3-array";
import { scaleLinear } from "d3-scale";
import { area, curveStepAfter, type SeriesPoint, stack, stackOffsetDiverging } from "d3-shape";
import { type MouseEvent, type ReactNode, useId, useState } from "react";
import { formatCount } from "@/features/evals/components";
import { ChartAction } from "./ChartControls";
import { useDragZoom } from "./dragZoom";
import { FoldedBreakdown } from "./FoldedBreakdown";
import { highlights, useHighlight } from "./highlight";
import { BreakMark, foldSmallestSeries, typicalSpan } from "./StackedBars";
import {
  type BucketStep,
  type ChartBucket,
  type ChartSeries,
  chartHeight,
  chartMargin,
  chartWidth,
  EVERYTHING_ELSE,
  everythingElseShade,
  formatTimeTick,
  shadeOf,
  svgId,
  timeScale,
} from "./shared";

const momentFormat = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
  timeZoneName: "short",
});

/** The least width, in chart units, the latest step keeps at the right edge, so it can be seen and hovered. */
const LATEST_WIDTH = 3;

const startOf = bisector((point: ChartBucket<number>) => Date.parse(point.start)).right;

/**
 * Series stacked as areas over time, first at the bottom, each point's values holding flat until
 * the next point and the last until `to`. Time runs to scale from `from` to `to`, and the axis
 * reads in the zone `step` reads in. Hovering anywhere reads the values at that moment, and the
 * legend and tags bring series forward and pull folded ones out as in {@link StackedBars}. With
 * `onSelect`, clicking picks the point under the pointer; with `onZoom`, dragging across picks a
 * span. `zoomed` fits the axis to the lowest and highest totals rather than running it from zero,
 * so the series at the bottom may be cut off below it; the readout and legend still read their
 * whole values.
 */
export function StackedSteps({
  title,
  series: givenSeries,
  points: givenPoints,
  from,
  to,
  step,
  summary,
  format,
  formatTick = formatCount,
  rest = EVERYTHING_ELSE,
  zoomed = false,
  onSelect,
  onZoom,
  fitted: givenFitted,
  onFittedChange,
}: {
  title: string;
  /** The first stacks at the bottom. Past the most a chart names, the ones that move least fold. */
  series: readonly ChartSeries[];
  /** Each point's values, from its start until the next point's, oldest first. */
  points: readonly ChartBucket<number>[];
  from: number;
  to: number;
  step: BucketStep;
  /** The readout while nothing is hovered. */
  summary: ReactNode;
  format: (value: number) => string;
  formatTick?: (value: number) => string;
  rest?: ChartSeries;
  zoomed?: boolean;
  onSelect?: (point: number) => void;
  /** The span dragged across. */
  onZoom?: (from: number, to: number) => void;
  /** Whether "Fit typical" is on, for a caller that keeps it; the chart keeps it otherwise. */
  fitted?: boolean;
  onFittedChange?: (fitted: boolean) => void;
}) {
  const [hoveredAt, setHoveredAt] = useState<number | null>(null);
  const [foldedHovered, setFoldedHovered] = useState<"point" | "latest" | null>(null);
  const [legendKey, setLegendKey] = useState<string | null>(null);
  const [ownFitted, setOwnFitted] = useState(false);
  const fitted = givenFitted ?? ownFitted;
  const setFitted = onFittedChange ?? setOwnFitted;
  const tagged = useHighlight().keys;
  const hatchId = `${svgId(useId())}-hatch`;
  const dragZoom = useDragZoom(onZoom);
  const {
    series,
    buckets: points,
    folded,
    pulledCount,
    restIndex,
  } = foldSmallestSeries(
    givenSeries,
    givenPoints,
    rest,
    entry => tagged !== null && highlights(tagged, entry),
  );
  const highlightKeys = legendKey === null ? tagged : [legendKey];
  const forward = series.map(entry => highlightKeys !== null && highlights(highlightKeys, entry));
  const dimming = forward.some(Boolean);
  if (points.length === 0) return null;

  const x = timeScale(step)
    .domain([new Date(from), new Date(to)])
    .range([chartMargin.left, chartWidth - chartMargin.right]);
  const right = chartWidth - chartMargin.right;
  const xs = points.map(point =>
    Math.min(x(new Date(point.start)) ?? chartMargin.left, right - LATEST_WIDTH),
  );
  const layers = stack<ChartBucket<number>, number>()
    .keys(series.map((_, index) => index))
    .value((point, index) => point.values[index] ?? 0)
    .offset(stackOffsetDiverging)(points);
  const tops = points.map((_, index) => max(layers, layer => layer[index]?.[1]) ?? 0);
  const bottoms = points.map((_, index) => min(layers, layer => layer[index]?.[0]) ?? 0);
  const lasts = points.map((point, index) => {
    const next = points[index + 1];
    return (next ? Date.parse(next.start) : to) - Math.max(Date.parse(point.start), from);
  });
  const typical = typicalSpan(tops, bottoms, lasts);
  const fit = fitted && typical?.cuts === true;
  const highest = Math.max(0, (fit ? typical.top : max(tops)) ?? 0);
  const lowestTop = Math.min(min(tops) ?? 0, highest);
  const pad =
    highest > lowestTop ? (highest - lowestTop) * 0.1 : Math.max(Math.abs(highest) * 0.01, 0.0001);
  const lowest = zoomed
    ? Math.max(0, lowestTop - pad)
    : Math.min(0, (fit ? typical.bottom : min(bottoms)) ?? 0);
  const y = scaleLinear()
    .domain([lowest, zoomed ? highest + pad : highest === lowest ? lowest + 1 : highest])
    .nice(4)
    .range([chartHeight - chartMargin.bottom, chartMargin.top])
    .clamp(true);
  if (!zoomed) y.domain([lowest, y.domain()[1] ?? highest]);
  const ends = [...xs.slice(1), right];
  const shape = area<[SeriesPoint<ChartBucket<number>>, number]>()
    .x(([, x]) => x)
    .y0(([point]) => y(point[0]))
    .y1(([point]) => y(point[1]))
    .curve(curveStepAfter);
  const pathOf = (layer: (typeof layers)[number]) => {
    const last = layer.at(-1);
    const corners = layer.map((point, index) => [point, xs[index] ?? 0] as [typeof point, number]);
    return shape(last ? [...corners, [last, right]] : corners) ?? "";
  };
  const hovered = hoveredAt === null ? -1 : startOf(points, hoveredAt) - 1;
  const totals = points.map(point => sum(point.values));
  const pulledFrom = restIndex - pulledCount;
  const isPulled = (index: number) => index >= pulledFrom && index < restIndex;
  const shadeFill = (index: number) =>
    index === restIndex ? "" : isPulled(index) ? everythingElseShade.fill : shadeOf(index).fill;
  const shownIndex = hovered >= 0 ? hovered : null;

  const breakdownAt = foldedHovered === "latest" ? points.length - 1 : shownIndex;
  const breakdown =
    foldedHovered && breakdownAt !== null
      ? {
          title:
            foldedHovered === "latest"
              ? `${rest.label}, latest`
              : `${rest.label}, ${momentFormat.format(new Date(hoveredAt ?? 0))}`,
          items: folded
            .map(index => ({ index, value: givenPoints[breakdownAt]?.values[index] ?? 0 }))
            .filter(item => item.value !== 0)
            .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
            .map(item => ({
              key: givenSeries[item.index]?.key ?? String(item.index),
              label: givenSeries[item.index]?.label ?? "",
              value: format(item.value),
            })),
        }
      : null;

  const hover = (event: MouseEvent<SVGRectElement>) => {
    const box = event.currentTarget.ownerSVGElement?.getBoundingClientRect();
    if (!box || box.width === 0 || box.height === 0) return;
    const left = ((event.clientX - box.left) / box.width) * chartWidth;
    const at =
      left >= right - LATEST_WIDTH ? to : Math.min(Math.max(x.invert(left).valueOf(), from), to);
    const value = y.invert(((event.clientY - box.top) / box.height) * chartHeight);
    const [low, high] = layers[restIndex]?.[startOf(points, at) - 1] ?? [0, 0];
    setHoveredAt(at);
    setFoldedHovered(value >= Math.min(low, high) && value <= Math.max(low, high) ? "point" : null);
  };

  return (
    <div className="relative">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm tabular-nums">
          {shownIndex !== null && hoveredAt !== null
            ? `${format(totals[shownIndex] ?? 0)} · ${momentFormat.format(new Date(hoveredAt))}`
            : summary}
          {shownIndex !== null && onSelect && (
            <span className="text-muted-foreground"> · Click to see these changes</span>
          )}
        </p>
        {typical && (
          <ChartAction>
            <button
              type="button"
              aria-pressed={fit}
              title={
                fit
                  ? "Scale to the highest point"
                  : "Scale to the typical points, cutting off the rest"
              }
              disabled={!typical.cuts}
              onClick={() => setFitted(!fit)}
              className={`shrink-0 disabled:pointer-events-none disabled:opacity-40 rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap text-muted-foreground ring-1 ring-inset ring-foreground/15 hover:text-foreground ${panelMotion} ${press} ${focusHaloFlush}`}
            >
              {fit ? "Show all" : "Fit typical"}
            </button>
          </ChartAction>
        )}
      </div>
      {breakdown?.items.some(item => item.key !== rest.key) && <FoldedBreakdown {...breakdown} />}
      <svg
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        className={`mt-3 w-full overflow-visible ${onZoom ? "select-none" : ""}`}
        role="img"
        onMouseLeave={() => {
          setHoveredAt(null);
          setFoldedHovered(null);
        }}
        {...dragZoom.svgProps(chartX => x.invert(chartX).valueOf())}
      >
        <title>{title}</title>
        <defs>
          <pattern
            id={hatchId}
            width={4}
            height={4}
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <line
              x1={0}
              y1={0}
              x2={0}
              y2={4}
              strokeWidth={1.5}
              className={everythingElseShade.hatch}
            />
          </pattern>
        </defs>
        {y.ticks(4).map(tick => (
          <g key={tick}>
            <line
              x1={chartMargin.left}
              x2={right}
              y1={y(tick)}
              y2={y(tick)}
              className="stroke-foreground/[0.07]"
            />
            <text
              x={chartMargin.left - 10}
              y={y(tick) + 4}
              fontSize={11}
              textAnchor="end"
              className="fill-muted-foreground tabular-nums"
            >
              {formatTick(tick)}
            </text>
          </g>
        ))}
        {x.ticks(6).map(tick => (
          <text
            key={tick.valueOf()}
            x={x(tick)}
            y={chartHeight - chartMargin.bottom + 18}
            fontSize={11}
            textAnchor="middle"
            className="fill-muted-foreground tabular-nums"
          >
            {formatTimeTick(tick, step)}
          </text>
        ))}
        {layers.map((layer, seriesIndex) => (
          <path
            key={series[seriesIndex]?.key}
            d={pathOf(layer)}
            className={`${panelMotion} ${shadeFill(seriesIndex)} ${dimming && !forward[seriesIndex] ? "opacity-25" : ""}`}
            fill={seriesIndex === restIndex ? `url(#${hatchId})` : undefined}
          />
        ))}
        {fit &&
          points.map((point, index) =>
            (tops[index] ?? 0) > (y.domain()[1] ?? 0) ? (
              <g key={point.start} className="pointer-events-none">
                <BreakMark
                  left={xs[index] ?? 0}
                  width={(ends[index] ?? right) - (xs[index] ?? 0)}
                  y={chartMargin.top + 6}
                />
              </g>
            ) : null,
          )}
        {hoveredAt !== null && (
          <line
            x1={x(new Date(hoveredAt))}
            x2={x(new Date(hoveredAt))}
            y1={chartMargin.top}
            y2={chartHeight - chartMargin.bottom}
            className="pointer-events-none stroke-foreground/40"
          />
        )}
        {dragZoom.band}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: hovering changes the readout above, and clicking is a shortcut the history list also offers */}
        <rect
          x={chartMargin.left}
          y={chartMargin.top}
          width={right - chartMargin.left}
          height={chartHeight - chartMargin.top - chartMargin.bottom}
          className={`fill-transparent ${onSelect ? "cursor-pointer" : ""}`}
          onMouseMove={hover}
          onClick={() => hovered >= 0 && onSelect?.(hovered)}
        />
      </svg>
      {series.length > 1 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {series.map((entry, index) =>
            isPulled(index) ? null : (
              // biome-ignore lint/a11y/noStaticElementInteractions: hovering only brings the series forward and shows what the folded rest holds
              <span
                key={entry.key}
                className="flex items-center gap-1.5"
                onMouseEnter={() => {
                  setLegendKey(entry.key);
                  if (index === restIndex) setFoldedHovered("latest");
                }}
                onMouseLeave={() => {
                  setLegendKey(null);
                  if (index === restIndex) setFoldedHovered(null);
                }}
              >
                <span
                  className={`size-2 rounded-[2px] ${index === restIndex ? everythingElseShade.swatch : shadeOf(index).swatch}`}
                />
                {entry.label}
                {shownIndex !== null && (
                  <span className="tabular-nums text-foreground">
                    {format(points[shownIndex]?.values[index] ?? 0)}
                  </span>
                )}
              </span>
            ),
          )}
        </div>
      )}
    </div>
  );
}
